import { FC, useMemo, useState } from "react";
import {
  Stack,
  Typography,
  TableContainer,
  Table as MUITable,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { Loading } from "react-admin";
import { useAffiliateEarnings } from "@/queries";
import { AffiliateEarningRow } from "./affiliate-earnings-row.component";

const AffiliateEarnings: FC<{ affiliateId: string }> = ({ affiliateId }) => {
  const [selectedYear, setSelectedYear] = useState<Dayjs>(dayjs());

  const {
    isAffiliateEarningsLoading,
    affiliateEarnings,
    affiliateEarningsError,
  } = useAffiliateEarnings({
    affiliateId: affiliateId ?? "",
    fromYear: selectedYear.year(),
    toYear: selectedYear.year(),
    fromMonth: 1,
    toMonth: 12,
  });

  const renderPicker = useMemo(
    () => (
      <DatePicker
        label="Select Year"
        views={["year"]}
        value={selectedYear}
        onChange={(date) => date && setSelectedYear(date)}
        disableFuture
        minDate={dayjs("2024-01-01")}
        slotProps={{
          textField: {
            sx: {
              width: "auto",
              "& .MuiPickersInputBase-sectionsContainer": {
                width: "auto",
              },
            },
          },
        }}
      />
    ),
    [selectedYear],
  );

  if (isAffiliateEarningsLoading) {
    return <Loading />;
  }

  if (affiliateEarningsError) {
    return null;
  }

  return (
    <Stack width="50%" spacing={2}>
      <Typography variant="h6" gutterBottom>
        Earnings
      </Typography>

      {affiliateEarnings.length === 0 ? (
        <Box display={"flex"} alignItems={"center"} gap={1}>
          <Typography>No data</Typography>
          {renderPicker}
        </Box>
      ) : (
        <TableContainer>
          <MUITable aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell>Month</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell align={"right"}>{renderPicker}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {affiliateEarnings.map((affiliateEarning, index) => (
                <AffiliateEarningRow
                  key={index}
                  affiliateId={affiliateId || ""}
                  {...affiliateEarning}
                />
              ))}
            </TableBody>
          </MUITable>
        </TableContainer>
      )}
    </Stack>
  );
};

export { AffiliateEarnings };
